import axios from "axios";

// Cấu hình S3 từ environment variables hoặc constants
const S3_CONFIG = {
  region: process.env.REACT_APP_AWS_REGION || "ap-southeast-1",
  bucket: process.env.REACT_APP_S3_BUCKET_NAME,
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
};

class S3Uploader {
  constructor() {
    // Validate S3 config
    if (
      !S3_CONFIG.bucket ||
      !S3_CONFIG.accessKeyId ||
      !S3_CONFIG.secretAccessKey
    ) {
      console.warn(
        "S3 configuration is incomplete. Some upload features may not work."
      );
    }
  }

  /**
   * Tạo S3 key với cấu trúc folder theo task ID
   * @param {string} taskId - ID của task
   * @param {string} filename - Tên file
   * @param {string} subfolder - Subfolder (optional)
   * @returns {string} S3 key
   */
  getS3Key(taskId, filename, subfolder = null) {
    const keyParts = ["user_uploads", taskId];

    if (subfolder) {
      keyParts.push(subfolder);
    }

    keyParts.push(filename);
    return keyParts.join("/");
  }

  /**
   * Upload file trực tiếp lên S3 thông qua presigned URL
   * @param {File} file - File object
   * @param {string} taskId - ID của task
   * @param {string} subfolder - Subfolder (optional)
   * @returns {Promise<Object>} Upload result
   */
  async uploadFile(file, taskId, subfolder = null) {
    try {
      // Method 1: Upload trực tiếp qua backend (ưu tiên)
      try {
        const uploadResult = await this.uploadViaBackend(
          file,
          taskId,
          subfolder
        );

        if (uploadResult.success) {
          return {
            success: true,
            filename: uploadResult.filename,
            s3_url: uploadResult.s3_url,
            s3_bucket: uploadResult.s3_bucket,
            s3_key: uploadResult.s3_key,
            content_type: uploadResult.content_type,
            file_size: uploadResult.file_size,
            upload_time: uploadResult.upload_time,
            subfolder: subfolder,
            task_id: taskId,
          };
        }
      } catch (backendError) {
        console.warn(
          "Backend upload failed, trying presigned URL:",
          backendError
        );
      }

      // Method 2: Fallback sử dụng presigned URL
      const s3Key = this.getS3Key(taskId, file.name, subfolder);
      const uploadResult = await this.uploadViaPresignedUrl(
        file,
        s3Key,
        taskId
      );

      if (uploadResult) {
        return {
          success: true,
          filename: file.name,
          s3_url: uploadResult.s3_url,
          s3_bucket: S3_CONFIG.bucket,
          s3_key: s3Key,
          content_type: file.type,
          file_size: file.size,
          upload_time: new Date().toISOString(),
          subfolder: subfolder,
          task_id: taskId,
        };
      }

      throw new Error("Upload failed");
    } catch (error) {
      console.error("S3 upload error:", error);
      return {
        success: false,
        error: error.message,
        filename: file.name,
      };
    }
  }

  /**
   * Upload file thông qua presigned URL từ backend
   * @param {File} file - File object
   * @param {string} s3Key - S3 key
   * @param {string} taskId - Task ID
   * @returns {Promise<Object>} Upload result
   */
  async uploadViaPresignedUrl(file, s3Key, taskId) {
    try {
      // Bước 1: Lấy presigned URL từ backend
      const presignedResponse = await axios.post("/api/get-presigned-url", {
        key: s3Key,
        content_type: file.type,
        task_id: taskId,
      });

      if (!presignedResponse.data.success) {
        throw new Error("Failed to get presigned URL");
      }

      const { presigned_url, fields } = presignedResponse.data;

      // Bước 2: Upload file lên S3 sử dụng presigned URL
      const formData = new FormData();

      // Thêm các fields từ presigned URL
      Object.keys(fields).forEach((key) => {
        formData.append(key, fields[key]);
      });

      // Thêm file (phải là cuối cùng)
      formData.append("file", file);

      const uploadResponse = await axios.post(presigned_url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (uploadResponse.status === 204 || uploadResponse.status === 200) {
        return {
          s3_url: `https://${S3_CONFIG.bucket}.s3.${S3_CONFIG.region}.amazonaws.com/${s3Key}`,
        };
      }

      throw new Error("S3 upload failed");
    } catch (error) {
      console.error("Presigned URL upload error:", error);
      throw error;
    }
  }

  /**
   * Upload file thông qua backend API (alternative method)
   * @param {File} file - File object
   * @param {string} taskId - Task ID
   * @param {string} subfolder - Subfolder (optional)
   * @returns {Promise<Object>} Upload result
   */
  async uploadViaBackend(file, taskId, subfolder = null) {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("task_id", taskId);

      if (subfolder) {
        formData.append("subfolder", subfolder);
      }

      const response = await axios.post("/api/s3/upload/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data;
    } catch (error) {
      console.error("Backend S3 upload error:", error);
      throw error;
    }
  }

  /**
   * Upload multiple files
   * @param {FileList|Array} files - Files to upload
   * @param {string} taskId - Task ID
   * @param {string} subfolder - Subfolder (optional)
   * @param {function} onProgress - Progress callback
   * @returns {Promise<Array>} Array of upload results
   */
  async uploadMultipleFiles(
    files,
    taskId,
    subfolder = null,
    onProgress = null
  ) {
    const results = [];
    const fileArray = Array.from(files);

    for (let i = 0; i < fileArray.length; i++) {
      const file = fileArray[i];

      try {
        if (onProgress) {
          onProgress({
            current: i + 1,
            total: fileArray.length,
            filename: file.name,
            status: "uploading",
          });
        }

        const result = await this.uploadFile(file, taskId, subfolder);
        results.push(result);

        if (onProgress) {
          onProgress({
            current: i + 1,
            total: fileArray.length,
            filename: file.name,
            status: result.success ? "success" : "error",
            result: result,
          });
        }
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        results.push({
          success: false,
          filename: file.name,
          error: error.message,
        });

        if (onProgress) {
          onProgress({
            current: i + 1,
            total: fileArray.length,
            filename: file.name,
            status: "error",
            error: error.message,
          });
        }
      }
    }

    return results;
  }

  /**
   * Tạo task ID unique cho mỗi session upload
   * @returns {string} Task ID
   */
  generateTaskId() {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    return `task_${timestamp}_${random}`;
  }

  /**
   * Get public URL cho S3 object
   * @param {string} s3Key - S3 key
   * @returns {string} Public URL
   */
  getPublicUrl(s3Key) {
    return `https://${S3_CONFIG.bucket}.s3.${S3_CONFIG.region}.amazonaws.com/${s3Key}`;
  }
}

// Export singleton instance
const s3Uploader = new S3Uploader();
export default s3Uploader;

// Export class for custom instances
export { S3Uploader };
